var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo } from 'react';
import { ModalLayout } from 'root/src/client/v2/common/layouts/ModalLayout';
import { ModalTitle } from 'root/src/client/v2/common/components/modal/ModalTitle';
import { FormProvider, useController, useForm, useFormContext } from 'react-hook-form';
import { deliveryDareVideoSchemaZ, } from 'root/src/shared/descriptions/formSchemas/deliveryDareVideoSchema';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import { ErrorField } from 'root/src/client/v2/_pages/add-campaign/components/AddCampaignForm/ErrorField';
import { ButtonNew } from 'root/src/client/v2/common/components/form/ButtonNew';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { getErrorMessage } from 'root/src/client/v2/common/hooks/useDareDropApi';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import LoadingCircles from 'root/src/client/v2/common/components/LoadingCircles';
import { useModal } from 'root/src/client/v2/common/contexts/modal.context';
import { getTimestampFromVideoURL } from 'root/src/client/logic/project/thunks/deliveryDare';
import { isApiAuthTRPCError } from 'root/src/shared/error/guards/isApiAuthError.guard';
import SignInWithProviderModal from 'root/src/client/web/auth/features/SignInWithProviderModal';
import { getReadableProviderName } from 'root/src/client/v2/auth/helpers/providerHelpers';
export const DeliverDareModal = memo((props) => {
    const { form, onSubmit } = useDeliverDareModal(props);
    return (<ModalLayout className="sm:w-[600px]">
			<ModalTitle>
				Deliver Dare
			</ModalTitle>
			<FormProvider {...form}>
				<form onSubmit={onSubmit} className="flex flex-col gap-4 w-full py-4">
					<VideoURLField />
					<NotesForReviewerField />
					<Submit />
				</form>
			</FormProvider>
		</ModalLayout>);
});
const useDeliverDareModal = (props) => {
    const { projectUrl } = props;
    const deliverMutation = trpc.dares.deliver.useMutation();
    const modal = useModal();
    const form = useForm({
        defaultValues: {
            videoURL: '',
            notesForReviewer: '',
        },
        resolver: zodResolver(deliveryDareVideoSchemaZ),
    });
    const onSubmit = form.handleSubmit((data) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        try {
            yield deliverMutation.mutateAsync({
                projectUrl,
                videoURL: data.videoURL,
                notesForReviewer: (_a = data.notesForReviewer) !== null && _a !== void 0 ? _a : undefined,
                timeStamp: getTimestampFromVideoURL(data.videoURL),
            });
            toast.success('Dare delivered successfully!');
            yield ((_b = props === null || props === void 0 ? void 0 : props.onSuccess) === null || _b === void 0 ? void 0 : _b.call(props));
            modal.hide();
        }
        catch (err) {
            if (isApiAuthTRPCError(err)) {
                const { provider } = err.data.props;
                modal.show(<ModalLayout>
						<SignInWithProviderModal props={{
                        item: {
                            popup: true,
                            popupOptions: {
                                onSuccess: () => {
                                    modal.show(<DeliverDareModal projectUrl={props.projectUrl} onSuccess={props === null || props === void 0 ? void 0 : props.onSuccess}/>);
                                    toast.success('Account successfully refreshed!');
                                },
                                onError: (error) => {
                                    console.error(error);
                                    toast.error((error === null || error === void 0 ? void 0 : error.message) || 'Something went wrong!');
                                },
                            },
                            provider,
                            header: 'Sign in to continue',
                            label: `Please re-authenticate your ${getReadableProviderName(provider)} account to deliver this Dare.`,
                        },
                    }}/>
					</ModalLayout>);
            }
            console.error('Failed to deliver dare', err);
            const message = getErrorMessage(err);
            toast.error(message);
        }
    }));
    return {
        form,
        onSubmit,
    };
};
const useDeliverDareFormContext = () => {
    return useFormContext();
};
const VideoURLField = memo(() => {
    var _a;
    const form = useDeliverDareFormContext();
    const { field, fieldState } = useController({
        name: 'videoURL',
        control: form.control,
    });
    return (<FormControl error={Boolean(fieldState.error)}>
			<TextField {...field} required autoFocus label="Video URL" error={Boolean(fieldState.error)} helperText="Link to the video of the completed dare. Make sure to point to the exact moment the dare was completed."/>
			<ErrorField error={(_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message}/>
		</FormControl>);
});
const NotesForReviewerField = memo(() => {
    var _a;
    const form = useDeliverDareFormContext();
    const { field, fieldState } = useController({
        name: 'notesForReviewer',
        control: form.control,
    });
    return (<FormControl error={Boolean(fieldState.error)}>
			<TextField label="Notes for Reviewer" helperText="Optionally provide notes for the reviewer." multiline rows={3} {...field}/>
			<ErrorField error={(_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message}/>
		</FormControl>);
});
const Submit = memo(() => {
    const form = useDeliverDareFormContext();
    return (<ButtonNew type="submit" disabled={form.formState.isSubmitting}>
			{form.formState.isSubmitting ? <LoadingCircles width={25} height={25}/> : <>Submit</>}
		</ButtonNew>);
});
